/**
 * Cliente HTTP del frontend. Envuelve fetch con cookies para mantener
 * la sesión del backend.
 */

export interface ApiError {
  error: string;
  details?: unknown;
}

export class ApiException extends Error {
  constructor(
    public status: number,
    public error: string,
    public details?: unknown,
  ) {
    super(error);
    this.name = 'ApiException';
  }
}

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
const SESSION_INVALID_ERRORS = new Set(['session_expired', 'unauthenticated', 'user_inactive']);

let sessionInvalidHandler: ((reason: string) => void) | null = null;

export function setSessionInvalidHandler(handler: (reason: string) => void): void {
  sessionInvalidHandler = handler;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
    ...options,
  });

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const err = body as ApiError | null;
    const exception = new ApiException(response.status, err?.error ?? 'unknown_error', err?.details);
    if (exception.status === 401 && SESSION_INVALID_ERRORS.has(exception.error)) {
      sessionInvalidHandler?.(exception.error);
    }
    throw exception;
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
